import { Directive, computed, input } from '@angular/core';
import {
  SURFACE_ELEVATIONS,
  SURFACE_EMPHASES,
  SURFACE_SIZES,
  SURFACE_TONES,
  SURFACE_VARIANTS,
  type SurfaceElevation,
  type SurfaceEmphasis,
  type SurfaceSize,
  type SurfaceTone,
  type SurfaceVariant,
} from '@groove/core';

// The axis vocabularies are owned by @groove/core (framework-neutral, so the
// coverage engine can read them without pulling in @angular/core). Re-exported
// here so consumers can keep importing them from the directive. Note that
// elevation and emphasis are part of the family's vocabulary but not of the
// coverage cell product — see groove-surface.axes.ts in @groove/core.
export {
  SURFACE_ELEVATIONS,
  SURFACE_EMPHASES,
  SURFACE_SIZES,
  SURFACE_TONES,
  SURFACE_VARIANTS,
  type SurfaceElevation,
  type SurfaceEmphasis,
  type SurfaceSize,
  type SurfaceTone,
  type SurfaceVariant,
};

@Directive({
  selector: '[grooveSurface]',
  standalone: true,
  host: {
    class: 'groove-surface',
    '[class.groove-ring]': 'interactive()',
    '[attr.data-interactive]': 'interactive() ? "true" : null',
    '[attr.data-variant]': 'variantAttr()',
    '[attr.data-size]': 'sizeAttr()',
    '[attr.data-tone]': 'toneAttr()',
    '[attr.data-elevation]': 'elevationAttr()',
    '[attr.data-emphasis]': 'emphasisAttr()',
  },
})
export class GrooveSurfaceDirective {
  readonly variant = input<SurfaceVariant>('default');
  readonly size = input<SurfaceSize>('md');
  readonly tone = input<SurfaceTone>('default');
  readonly elevation = input<SurfaceElevation>(0);
  readonly interactive = input(false);
  readonly emphasis = input<SurfaceEmphasis>('low');

  // Omit default attrs to keep DOM clean.
  protected readonly variantAttr = computed(() =>
    this.variant() === 'default' ? null : this.variant(),
  );
  protected readonly sizeAttr = computed(() =>
    this.size() === 'md' ? null : this.size(),
  );
  protected readonly toneAttr = computed(() =>
    this.tone() === 'default' ? null : this.tone(),
  );
  protected readonly elevationAttr = computed(() =>
    this.elevation() === 0 ? null : String(this.elevation()),
  );
  protected readonly emphasisAttr = computed(() =>
    this.emphasis() === 'high' ? 'high' : null,
  );
}
